import { z } from "zod";
import { serviceItemKindSchema, serviceTypeSchema, SERVICE_TYPES } from "./enums.js";
import { amountSchema, isoDateSchema, odometerSchema } from "./primitives.js";
export const serviceItemInputSchema = z.object({
    kind: serviceItemKindSchema.default("PART"),
    name: z.string().trim().min(1, "Name the part or work done").max(120),
    partNo: z.string().trim().max(60).optional(),
    quantity: z.coerce.number().positive().max(9999).default(1),
    unitCost: amountSchema.optional(),
    amount: amountSchema.optional(),
    warrantyMonths: z.coerce.number().int().min(0).max(240).optional(),
});
export const serviceInputSchema = z.object({
    type: serviceTypeSchema,
    title: z.string().trim().min(1, "Give this service a short title").max(140),
    serviceDate: isoDateSchema,
    durationDays: z.coerce.number().int().min(0).max(365).default(1),
    odometerKm: odometerSchema.optional(),
    totalCost: amountSchema,
    labourCost: amountSchema.optional(),
    partsCost: amountSchema.optional(),
    centreName: z.string().trim().max(120).optional(),
    centreCity: z.string().trim().max(80).optional(),
    nextDueDate: isoDateSchema.optional(),
    nextDueKm: odometerSchema.optional(),
    notes: z.string().trim().max(4000).optional(),
    /**
     * Optional on purpose. Requiring line items for a ₹300 wash is how a
     * service log dies in month two.
     */
    items: z.array(serviceItemInputSchema).max(50).default([]),
});
export const serviceUpdateSchema = serviceInputSchema.partial();
const SORT_FIELDS = [
    "date:desc",
    "date:asc",
    "cost:desc",
    "cost:asc",
    "odometer:desc",
    "odometer:asc",
];
/** Query-string contract for the filtered listing. Everything is optional. */
export const serviceFilterSchema = z
    .object({
    year: z.coerce.number().int().min(1950).max(2100).optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    types: z
        .string()
        .optional()
        .transform((v) => v
        ? v
            .split(",")
            .map((t) => t.trim().toUpperCase())
            .filter((t) => SERVICE_TYPES.includes(t))
        : undefined),
    minCost: amountSchema.optional(),
    maxCost: amountSchema.optional(),
    q: z.string().trim().min(1).max(100).optional(),
    sort: z.enum(SORT_FIELDS).default("date:desc"),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
})
    .refine((f) => !f.from || !f.to || f.from <= f.to, { message: "The start date must be before the end date", path: ["to"] });
/** `year=2025` is sugar for a full-year range. Resolved server-side. */
export function resolveDateRange(f) {
    if (f.year && !f.from && !f.to) {
        return { from: `${f.year}-01-01`, to: `${f.year}-12-31` };
    }
    return { from: f.from, to: f.to };
}
/** Indian financial year: 1 April to 31 March. The range you want at tax time. */
export function financialYearRange(startYear) {
    return { from: `${startYear}-04-01`, to: `${startYear + 1}-03-31` };
}
//# sourceMappingURL=service.js.map